/**
 * <s-variant-image> — Swatch-driven variant picker and thumbnail gallery for the homepage landing hero.
 *
 * Required child markup:
 *   input[data-variant-option] — Radio per variant with data-variant-id, data-image-src,
 *                                data-image-srcset, data-label.
 *   input[name="id"]           — Hidden submitted variant id; kept in sync here.
 *   [data-variant-image] img   — Hero image swapped to the selected variant's or thumbnail's image.
 *   [data-variant-label]       — Text node showing the selected color name.
 *   button[data-thumb]         — Optional thumbnails with data-image-src, data-image-srcset and aria-pressed.
 */
class SVariantImage extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('change', (event) => {
      const option = event.target;
      if (!(option instanceof HTMLInputElement) || !option.matches('[data-variant-option]')) return;
      this.#select(option);
    }, opts);

    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const thumb = event.target.closest('button[data-thumb]');
      if (!thumb || !this.contains(thumb)) return;

      // A thumbnail tied to a variant selects that color; other photos only change the hero image.
      const variantId = thumb.dataset.variantId;
      const option = variantId ? this.querySelector(`input[data-variant-option][data-variant-id="${variantId}"]`) : null;
      if (option instanceof HTMLInputElement && !option.disabled) {
        option.checked = true;
        this.#select(option);
        return;
      }
      this.#showImage(thumb.dataset.imageSrc, thumb.dataset.imageSrcset);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #select(option) {
    const idInput = this.querySelector('input[type="hidden"][name="id"]');
    if (idInput) idInput.value = option.dataset.variantId;

    const label = this.querySelector('[data-variant-label]');
    if (label) label.textContent = option.dataset.label || '';

    this.#showImage(option.dataset.imageSrc, option.dataset.imageSrcset);
  }

  #showImage(src, srcset) {
    if (!src) return;

    for (const thumb of this.querySelectorAll('button[data-thumb]')) {
      thumb.setAttribute('aria-pressed', String(thumb.dataset.imageSrc === src));
    }

    const img = this.querySelector('[data-variant-image] img');
    if (!img || img.getAttribute('src') === src) return;

    const swap = () => {
      img.srcset = srcset || '';
      img.src = src;
    };

    // Crossfade the swap where motion is allowed; otherwise switch instantly.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !img.animate) {
      swap();
      return;
    }
    img.getAnimations().forEach((animation) => animation.cancel());
    img.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150, easing: 'cubic-bezier(0.2, 0, 0, 1)' }).finished.then(() => {
      swap();
      img.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 250, easing: 'cubic-bezier(0.2, 0, 0, 1)' });
    }).catch(() => {});
  }
}

if (!customElements.get('s-variant-image')) customElements.define('s-variant-image', SVariantImage);
